import { Box, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { STATUS } from "../../../../theme/tokens";
import type { FacilityAnnouncementSummary } from "../../../../utils/models";
import { ThreadPreviewListItem } from "../styled";

type Props = {
	announcement: FacilityAnnouncementSummary;
	facilityName: string;
	/** The building's departments by id, named as under the building ("Dietary"). */
	departmentNameById: ReadonlyMap<string, string>;
	timezone: string;
};

/** One announcement: what it said, who it went to, who sent it when, and how its texts went so far. */
export const FacilityAnnouncementItem: FC<Props> = ({ announcement, facilityName, departmentNameById, timezone }) => {
	const { t } = useTranslation();
	const audience =
		announcement.departmentIds === null
			? t("announcements.to.building", { facility: facilityName })
			: t("announcements.to.departments", {
					// DEFAULT: a department since moved out of the building is no longer listed under it; its id still says which.
					departments: announcement.departmentIds.map((departmentId) => departmentNameById.get(departmentId) ?? departmentId).join(", "),
				});
	const sender = announcement.senderFirstName ?? t("announcements.sender.unknown");

	return (
		<ThreadPreviewListItem data-testid={`facility-announcement-${announcement.id}`}>
			<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, p: 1.5 }}>
				<Typography variant="body2" sx={{ fontWeight: 600 }}>
					{audience}
				</Typography>
				{announcement.narrowedByFilters && (
					<Typography variant="caption" sx={{ color: "text.secondary" }} data-testid={`facility-announcement-filtered-${announcement.id}`}>
						{t("announcements.filtered")}
					</Typography>
				)}
				<Typography variant="body2" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
					{announcement.message}
				</Typography>
				<Typography variant="caption" sx={{ color: "text.secondary" }}>
					{t("announcements.sent.by", { name: sender, time: announcement.createdAt.setZone(timezone).toFormat("MMM d, h:mm a") })}
				</Typography>
				<Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
					<Typography variant="caption" data-testid={`facility-announcement-delivered-${announcement.id}`}>
						{t("announcements.delivered", { delivered: announcement.delivered, texted: announcement.texted })}
					</Typography>
					{announcement.failed > 0 && (
						<Typography variant="caption" sx={{ color: STATUS.ERROR.main }} data-testid={`facility-announcement-failed-${announcement.id}`}>
							{t("announcements.failed", { count: announcement.failed })}
						</Typography>
					)}
				</Box>
			</Box>
		</ThreadPreviewListItem>
	);
};
